import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Contact,
  Globe,
  Shield,
  Settings,
  MessageSquare,
  CreditCard,
  BarChart3,
  Briefcase,
  PhoneCall,
  MessageCircle,
  Brain,
  UserCircle,
  Database,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Calendar,
  FileSearch,
  ClipboardList,
  FileText,
  CheckSquare
} from 'lucide-react';

// Navigation items by role
const getNavItems = (role, t) => {
  const ownerItems = [
    { id: 'owner-dashboard', path: '/', label: t('dashboard'), icon: BarChart3, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'follow-up', path: '/follow-up', label: t('followUpLabel', { defaultValue: 'Follow up' }), icon: PhoneCall, description: t('scheduledFollowUps', { defaultValue: 'Scheduled follow ups' }) },
    { id: 'approvals', path: '/approvals', label: 'Approvals', icon: CheckSquare, description: 'Review & approve requests' },
    { id: 'profiles', path: '/profiles', label: t('profiles'), icon: Contact, description: t('profileDirectory') || 'Client list directory' },
    { id: 'prequalifications', path: '/prequalifications', label: t('allLeads'), icon: Users, description: t('manageLeads') || 'Manage prequalifications' },
    { id: 'tenders', path: '/tenders', label: t('proposalTender') || 'Proposal / Tender', icon: FileText, description: t('proposalTenderPipeline') || 'Proposal & tender pipeline' },
    { id: 'contracts', path: '/contracts', label: t('contracts') || 'Contracts', icon: ClipboardList, description: t('paymentsAndInvoicing') || 'Contracts & payment tracking' },
    { id: 'audit-logs', path: '/audit-logs', label: t('auditLogs') || 'Audit Logs', icon: FileSearch, description: t('viewSystemAuditLogs') || 'Secure system audit trail' }
  ];

  const salesItems = [
    { id: 'employee', path: '/employee', label: t('dashboard'), icon: LayoutDashboard, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'follow-up', path: '/follow-up', label: t('followUpLabel', { defaultValue: 'Follow up' }), icon: PhoneCall, description: t('scheduledFollowUps', { defaultValue: 'Scheduled follow ups' }) },
    { id: 'profiles', path: '/profiles', label: t('profiles'), icon: Contact, description: t('profileDirectory') || 'Client list directory' },
    { id: 'prequalifications', path: '/prequalifications', label: t('myLeads'), icon: Users, description: t('myLeadsPipeline') || 'My prequalifications pipeline' },
    { id: 'tenders', path: '/tenders', label: t('proposalTender') || 'Proposal / Tender', icon: FileText, description: t('proposalTenderPipeline') || 'Proposal & tender pipeline' },
    { id: 'contracts', path: '/contracts', label: t('contracts') || 'Contracts', icon: ClipboardList, description: t('paymentsAndInvoicing') || 'Contracts & payment tracking' },
  ];

  const gmOperationsItems = [
    { id: 'owner-dashboard', path: '/', label: t('dashboard'), icon: BarChart3, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'follow-up', path: '/follow-up', label: t('followUpLabel', { defaultValue: 'Follow up' }), icon: PhoneCall, description: t('scheduledFollowUps', { defaultValue: 'Scheduled follow ups' }) },
    { id: 'approvals', path: '/approvals', label: 'Approvals', icon: CheckSquare, description: 'Review & approve requests' },
    { id: 'profiles', path: '/profiles', label: t('profiles'), icon: Contact, description: t('profileDirectory') || 'Client list directory' },
    { id: 'prequalifications', path: '/prequalifications', label: t('allLeads'), icon: Users, description: t('manageLeads') || 'Manage prequalifications' },
    { id: 'tenders', path: '/tenders', label: t('proposalTender') || 'Proposal / Tender', icon: FileText, description: t('proposalTenderPipeline') || 'Proposal & tender pipeline' },
    { id: 'contracts', path: '/contracts', label: t('contracts') || 'Contracts', icon: ClipboardList, description: t('paymentsAndInvoicing') || 'Contracts & payment tracking' },
  ];

  const bmOperationsItems = [
    { id: 'owner-dashboard', path: '/', label: t('dashboard'), icon: BarChart3, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'follow-up', path: '/follow-up', label: t('followUpLabel', { defaultValue: 'Follow up' }), icon: PhoneCall, description: t('scheduledFollowUps', { defaultValue: 'Scheduled follow ups' }) },
    { id: 'profiles', path: '/profiles', label: t('profiles'), icon: Contact, description: t('profileDirectory') || 'Client list directory' },
    { id: 'prequalifications', path: '/prequalifications', label: t('allLeads'), icon: Users, description: t('manageLeads') || 'Manage prequalifications' },
    { id: 'tenders', path: '/tenders', label: t('proposalTender') || 'Proposal / Tender', icon: FileText, description: t('proposalTenderPipeline') || 'Proposal & tender pipeline' },
    { id: 'contracts', path: '/contracts', label: t('contracts') || 'Contracts', icon: ClipboardList, description: t('paymentsAndInvoicing') || 'Contracts & payment tracking' },
  ];

  const teamLeadItems = [
    { id: 'owner-dashboard', path: '/', label: t('dashboard'), icon: BarChart3, description: t('analyticsAndKpis') || 'Analytics & KPIs' },
    { id: 'follow-up', path: '/follow-up', label: t('followUpLabel', { defaultValue: 'Follow up' }), icon: PhoneCall, description: t('scheduledFollowUps', { defaultValue: 'Scheduled follow ups' }) },
    { id: 'profiles', path: '/profiles', label: t('profiles'), icon: Contact, description: t('profileDirectory') || 'Client list directory' },
    { id: 'prequalifications', path: '/prequalifications', label: t('allLeads'), icon: Users, description: t('manageLeads') || 'Manage prequalifications' },
    { id: 'tenders', path: '/tenders', label: t('proposalTender') || 'Proposal / Tender', icon: FileText, description: t('proposalTenderPipeline') || 'Proposal & tender pipeline' },
    { id: 'contracts', path: '/contracts', label: t('contracts') || 'Contracts', icon: ClipboardList, description: t('paymentsAndInvoicing') || 'Contracts & payment tracking' },
  ];

  switch (role) {
    case 'CEO':
      return ownerItems;
    case 'GM_Operations':
      return gmOperationsItems;
    case 'BM_Operations':
      return bmOperationsItems;
    case 'Senior_Team_Lead':
    case 'Team_Lead':
      return teamLeadItems;
    case 'Associate_Team_Lead':
    case 'Associate':
      return salesItems;
    default:
      return ownerItems;
  }
};

import { useLanguage } from '../../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { useSocket } from '../../hooks/useSocket';

export default function RoleSidebar({
  activeTab,
  setActiveTab,
  userRole,
  onShowAdmin,
  onShowIntegrations,
  onShowUserSettings,
  onShowMigration,
  isMobile = false,
  collapsed = false,
  onToggleCollapse
}) {
  const { t } = useLanguage();
  const location = useLocation();
  const { socket } = useSocket();
  const navItems = getNavItems(userRole, t);
  const isOwner = userRole === 'CEO';
  const isCeoOrBmOps = ['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead'].includes(userRole);

  const handleNavClick = () => {
    if (isMobile && setActiveTab) {
      setActiveTab();
    }
  };

  // Mobile layout - simplified dropdown menu
  if (isMobile) {
    return (
      <div className="p-2">
        {/* Navigation Grid */}
        <div className="grid grid-cols-2 gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                ? 'bg-blue-100 text-blue-700 font-semibold'
                : 'bg-gray-50 text-gray-600 active:bg-gray-100'
                }`}
            >
              {({ isActive }) => (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="relative">
                    <item.icon
                      className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'
                        }`}
                    />
                  </div>
                  <span className="text-sm font-medium truncate">{item.label}</span>
                </div>
              )}
            </NavLink>
          ))}
        </div>

        {/* Admin Section */}
        {isCeoOrBmOps && (
          <>
            <div className="my-3 border-t border-gray-200"></div>
            <div className="grid grid-cols-2 gap-2">
              {isOwner && (
                <NavLink
                  to="/sources"
                  onClick={handleNavClick}
                  className={({ isActive }) => `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                    ? 'bg-blue-100 text-blue-700 font-semibold'
                    : 'bg-gray-50 text-gray-600 active:bg-gray-100'
                    }`}
                >
                  {({ isActive }) => (
                    <>
                      <Globe className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium truncate">{t('sources') || 'Sources'}</span>
                    </>
                  )}
                </NavLink>
              )}
              <NavLink
                to="/admin"
                onClick={handleNavClick}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                  ? 'bg-purple-100 text-purple-700 font-semibold'
                  : 'bg-purple-50 text-purple-700 active:bg-purple-100'
                  }`}
                style={!isOwner ? { gridColumn: 'span 2' } : {}}
              >
                <Shield className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-medium">{t('adminControlPanel')?.split(' ')[0] || 'Admin'}</span>
              </NavLink>
            </div>
          </>
        )}

        {/* User Settings */}
        <div className="my-3 border-t border-gray-200"></div>
        <button
          onClick={onShowUserSettings}
          className="w-full flex items-center gap-2 px-3 py-3 rounded-xl text-left bg-gray-100 text-gray-700 active:bg-gray-200"
        >
          <UserCircle className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium">{t('myAccount') || 'My Account'}</span>
        </button>
      </div>
    );
  }

  // Desktop layout - collapsible sidebar
  return (
    <aside className="w-full h-full bg-white flex flex-col relative">
      {/* Toggle Button */}
      <div className="absolute top-6 -right-4 z-50">
        <button
          onClick={onToggleCollapse}
          className="group flex items-center justify-center w-8 h-8 !min-h-[32px] rounded-full bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-sm"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
          ) : (
            <ChevronLeft className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 ${collapsed ? 'px-2 py-3' : 'p-4'} space-y-1 overflow-y-auto overflow-x-hidden`}>
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) => `
              group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
              ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
              ${isActive
                ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
              }`}
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <item.icon
                    className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                      }`}
                  />
                </div>
                {!collapsed && (
                  <div className="min-w-0 overflow-hidden flex-1 flex justify-between items-center pr-2">
                    <div>
                      <p className="text-sm font-medium truncate whitespace-nowrap">{item.label}</p>
                      {!isActive && (
                        <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{item.description}</p>
                      )}
                    </div>
                  </div>
                )}
                {/* Tooltip when collapsed */}
                {collapsed && (
                  <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                    pointer-events-none whitespace-nowrap z-50 shadow-lg">
                    {item.label}

                  </div>
                )}
              </>
            )}
          </NavLink>
        ))}

        {/* Admin Section */}
        {isCeoOrBmOps && (
          <>
            <div className="my-4 border-t border-gray-100"></div>
            {!collapsed && (
              <p className="px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 whitespace-nowrap overflow-hidden">
                {t('settings') || 'Settings'}
              </p>
            )}

            {/* Sources */}
            {isOwner && (
              <NavLink
                to="/sources"
                className={({ isActive }) => `
                  group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
                  ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
                  ${isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                {({ isActive }) => (
                  <>
                    <Globe className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                    {!collapsed && (
                      <div className="min-w-0 overflow-hidden">
                        <p className="text-sm font-medium truncate whitespace-nowrap">{t('sources') || 'Sources'}</p>
                        {!isActive && <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('manageAllSources') || 'Manage all sources'}</p>}
                      </div>
                    )}
                    {collapsed && (
                      <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                        pointer-events-none whitespace-nowrap z-50 shadow-lg">
                        {t('sources') || 'Sources'}

                      </div>
                    )}
                  </>
                )}
              </NavLink>
            )}

            {/* Admin Panel */}
            <NavLink
              to="/admin"
              className={({ isActive }) => `
                group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
                ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
                ${isActive || location.pathname === '/admin'
                  ? 'bg-purple-50 text-purple-700 font-semibold shadow-sm'
                  : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                }`}
            >
              {({ isActive }) => {
                const isCurrent = isActive || location.pathname === '/admin';
                return (
                  <>
                    <Shield className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isCurrent ? 'text-purple-600' : 'text-purple-500'}`} />
                    {!collapsed && (
                      <div className="min-w-0 overflow-hidden">
                        <p className="text-sm font-medium truncate whitespace-nowrap">{t('adminControlPanel') || 'Admin Panel'}</p>
                        {!isCurrent && <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('usersAndPermissions') || 'Users & permissions'}</p>}
                      </div>
                    )}
                    {collapsed && (
                      <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                        pointer-events-none whitespace-nowrap z-50 shadow-lg">
                        {t('adminControlPanel') || 'Admin Panel'}
                      </div>
                    )}
                  </>
                );
              }}
            </NavLink>

          </>
        )}
      </nav>

      {/* User Account */}
      <div className={`${collapsed ? 'p-2' : 'p-4'} border-t`}>
        <button
          onClick={onShowUserSettings}
          className={`group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 
            ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left text-gray-600 hover:bg-gray-100 transition-all duration-200`}
        >
          <UserCircle className="w-5 h-5 text-gray-500 flex-shrink-0" />
          {!collapsed && (
            <div className="min-w-0 overflow-hidden">
              <p className="text-sm font-medium truncate whitespace-nowrap">{t('myAccount') || 'My Account'}</p>
              <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('profileAndPassword') || 'Profile & password'}</p>
            </div>
          )}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
              opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
              pointer-events-none whitespace-nowrap z-50 shadow-lg">
              {t('myAccount') || 'My Account'}
            </div>
          )}
        </button>
      </div>
    </aside>
  );
}
